import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Mic, Moon, BookOpen, X, Volume2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";

/**
 * KidsSearch — full-screen kid-safe search overlay with an on-screen
 * keyboard (remote-friendly) + optional voice input. Queries `/tv/kids-search`
 * which is strictly filtered to is_kids_safe content.
 */
export function KidsSearch({ onClose, onPlay }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const recRef = useRef(null);

  useEffect(() => {
    const h = setTimeout(async () => {
      if (q.trim().length < 2) { setResults([]); return; }
      setLoading(true);
      try {
        const r = await api.get(`/tv/kids-search?q=${encodeURIComponent(q.trim())}`);
        setResults(r.data?.videos || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 280);
    return () => clearTimeout(h);
  }, [q]);

  const tap = (ch) => setQ((s) => (s + ch).slice(0, 40));
  const back = () => setQ((s) => s.slice(0, -1));
  const clear = () => setQ("");

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = "en-US";
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.onresult = (e) => setQ(e.results[0][0].transcript.toLowerCase().slice(0, 40));
    r.onend = () => setVoiceOn(false);
    r.onerror = () => setVoiceOn(false);
    recRef.current = r;
    setVoiceOn(true);
    try { r.start(); } catch { setVoiceOn(false); }
  };
  const stopVoice = () => { try { recRef.current?.stop?.(); } catch {} setVoiceOn(false); };
  useEffect(() => () => stopVoice(), []);

  const ROWS = useMemo(() => ([
    "abcdefghij".split(""),
    "klmnopqrst".split(""),
    "uvwxyz0123".split(""),
    "456789 -'".split(""),
  ]), []);

  return (
    <div data-testid="kids-search-overlay" className="fixed inset-0 z-50 flex flex-col items-center justify-start p-[3vmin] kids-night" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(236,72,153,0.35), transparent 55%), linear-gradient(180deg, #0b1336 0%, #1d1452 40%, #2b1650 100%)" }}>
      <div className="w-full max-w-6xl flex items-center gap-[1.6vmin] mb-[2vmin]">
        <Search className="w-[3vmin] h-[3vmin] min-w-[22px] text-white/70" />
        <div className="flex-1 text-white text-[3.2vmin] font-display font-black truncate bg-white/10 rounded-[1.4rem] px-[2vmin] py-[1.2vmin] backdrop-blur ring-2 ring-white/20">
          {q || <span className="text-white/40">Type or say a show…</span>}
        </div>
        <button data-tv-focus="true" data-testid="kids-search-voice" onClick={voiceOn ? stopVoice : startVoice}
          className={`tv-focusable kids-focusable w-[7vmin] h-[7vmin] min-w-[48px] min-h-[48px] rounded-full flex items-center justify-center shadow-lg ${voiceOn ? "bg-red-500 text-white animate-pulse" : "bg-yellow-300 text-[#222] hover:bg-yellow-200"}`}
          title={voiceOn ? "Stop" : "Say it"}
        ><Mic className="w-[3vmin] h-[3vmin] min-w-[22px]" /></button>
        <button data-tv-focus="true" data-testid="kids-search-close" onClick={onClose}
          className="tv-focusable kids-focusable w-[7vmin] h-[7vmin] min-w-[48px] min-h-[48px] rounded-full bg-white/90 hover:bg-white text-[#222] flex items-center justify-center shadow-lg"
        ><X className="w-[3vmin] h-[3vmin] min-w-[22px]" /></button>
      </div>

      {/* Results OR keyboard */}
      <div className="flex-1 w-full max-w-6xl overflow-y-auto">
        {q.trim().length >= 2 ? (
          loading ? (
            <div className="text-white/80 text-[2vmin] text-center mt-[6vmin]">Looking… ✨</div>
          ) : results.length === 0 ? (
            <div className="text-white/80 text-[2.4vmin] text-center mt-[6vmin]">No shows yet for "{q}". Try another word!</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-[1.6vmin]">
              {results.map((v) => (
                <button key={v.id} data-tv-focus="true" data-testid={`kids-search-item-${v.id}`}
                  onClick={() => { onPlay?.(v); onClose?.(); }}
                  className="tv-focusable kids-focusable text-left group"
                >
                  <div className="aspect-video bg-black rounded-[1.4rem] overflow-hidden ring-4 ring-white/20 group-hover:ring-pink-400 shadow-lg">
                    {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-fuchsia-500 to-sky-400" />}
                  </div>
                  <div className="mt-[0.8vmin] font-bold text-[1.8vmin] truncate text-white">{v.title}</div>
                  <div className="text-[1.4vmin] text-white/70 truncate">{v.creator_name || v.category}</div>
                </button>
              ))}
            </div>
          )
        ) : (
          /* On-screen kid-keyboard */
          <div className="flex flex-col items-center gap-[1.4vmin] mt-[2vmin]">
            {ROWS.map((row, ri) => (
              <div key={ri} className="flex gap-[1vmin]">
                {row.map((ch, ci) => (
                  <button key={ci} data-tv-focus="true" data-testid={`kids-key-${ch.trim() || "space"}`}
                    onClick={() => tap(ch)}
                    className="tv-focusable kids-focusable w-[7vmin] h-[7vmin] min-w-[48px] min-h-[48px] rounded-[1rem] bg-white/15 hover:bg-white/30 text-white text-[2.8vmin] font-black shadow-lg backdrop-blur"
                  >{ch === " " ? "␣" : ch.toUpperCase()}</button>
                ))}
              </div>
            ))}
            <div className="flex gap-[1vmin] mt-[1vmin]">
              <button data-tv-focus="true" data-testid="kids-key-back" onClick={back} className="tv-focusable kids-focusable px-[3vmin] h-[7vmin] min-h-[48px] rounded-[1rem] bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-[2vmin] font-black shadow-lg">⌫ Back</button>
              <button data-tv-focus="true" data-testid="kids-key-clear" onClick={clear} className="tv-focusable kids-focusable px-[3vmin] h-[7vmin] min-h-[48px] rounded-[1rem] bg-white/15 hover:bg-white/30 text-white text-[2vmin] font-black shadow-lg">Clear</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * KidsFlashcards — Learn & Play hub with decks + mini-games + official
 * Tap IN Learn & Play banner.
 *
 * Decks: ABC Letters · 123 Numbers · Animals · Colors · Shapes · First Words
 * Games: Matching (tap the pair) · Flashcards (auto-speak)
 *
 * Every card uses Web Speech synthesis for kid-friendly pronunciation, and
 * every deck card completion fires a `window.kidsLearnProgress` event so
 * the Parent Dashboard "My Kid's Week" can aggregate learning minutes.
 */
export function KidsFlashcards({ onClose }) {
  // ── Voice config ─────────────────────────────────────────────────────
  // `voicePreset`:
  //   "kid"   → browser TTS tuned to high-pitch, gentle rate
  //   "woman" → browser TTS tuned to a calm adult female (prefer female voice)
  //   "custom"→ play a parent-recorded intro (data-URL) on deck open, then
  //             fall back to browser TTS for individual cards
  const [voicePreset, setVoicePreset] = useState("kid");
  const [customVoiceUrl, setCustomVoiceUrl] = useState(null);
  const [introPlayed, setIntroPlayed] = useState(false);
  useEffect(() => {
    api.get("/kids/config").then((r) => {
      setVoicePreset(r.data?.voice_preset || "kid");
      setCustomVoiceUrl(r.data?.custom_voice_url || null);
    }).catch(() => {});
  }, []);

  const pickFemaleVoice = () => {
    try {
      const vs = window.speechSynthesis?.getVoices?.() || [];
      const en = vs.filter((v) => /^en[-_]/i.test(v.lang || "") || v.lang?.startsWith("en"));
      const fem = en.find((v) => /female|samantha|victoria|aria|jenny|zira|susan|karen|kate/i.test(v.name)) ||
                  en.find((v) => !/male/i.test(v.name));
      return fem || null;
    } catch { return null; }
  };
  const DECKS = useMemo(() => ({
    Letters: "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((L, i) => ({
      front: L + (L === "A" ? "a" : L.toLowerCase()),
      word: ["Apple","Bear","Cat","Dog","Elephant","Fish","Goat","Hat","Ice cream","Juice","Kite","Lion","Monkey","Nest","Owl","Pig","Queen","Rainbow","Sun","Tree","Umbrella","Van","Whale","Xylophone","Yellow","Zebra"][i],
      emoji: ["🍎","🐻","🐱","🐶","🐘","🐟","🐐","🎩","🍦","🧃","🪁","🦁","🐵","🪺","🦉","🐷","👸","🌈","☀️","🌳","☂️","🚐","🐋","🎼","💛","🦓"][i],
      color: "from-rose-400 via-pink-500 to-fuchsia-600",
      border: "border-emerald-400",
    })),
    Numbers: Array.from({length: 10}, (_, i) => ({
      front: String(i + 1),
      word: ["One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten"][i],
      emoji: ["🦆","🦆🦆","🐤🐤🐤","🐥🐥🐥🐥","⭐⭐⭐⭐⭐","🎈🎈🎈🎈🎈🎈","🌼🌼🌼🌼🌼🌼🌼","🐞🐞🐞🐞🐞🐞🐞🐞","🍒🍒🍒🍒🍒🍒🍒🍒🍒","🍎🍎🍎🍎🍎🍎🍎🍎🍎🍎"][i],
      color: "from-sky-400 via-cyan-500 to-teal-500",
      border: "border-sky-400",
    })),
    Animals: [
      { front: "Bear", emoji: "🐻", color: "from-amber-500 to-orange-700", border: "border-amber-400", word: "Bear — roar!", sound: "Bear" },
      { front: "Cat", emoji: "🐱", color: "from-orange-300 to-rose-500", border: "border-rose-400", word: "Cat — meow!", sound: "Cat" },
      { front: "Dog", emoji: "🐶", color: "from-amber-400 to-yellow-600", border: "border-yellow-400", word: "Dog — woof!", sound: "Dog" },
      { front: "Duck", emoji: "🦆", color: "from-yellow-400 to-amber-500", border: "border-yellow-400", word: "Duck — quack!", sound: "Duck" },
      { front: "Elephant", emoji: "🐘", color: "from-gray-400 to-gray-700", border: "border-gray-400", word: "Elephant — trumpet!", sound: "Elephant" },
      { front: "Lion", emoji: "🦁", color: "from-yellow-500 to-orange-700", border: "border-yellow-400", word: "Lion — roar!", sound: "Lion" },
      { front: "Monkey", emoji: "🐵", color: "from-amber-500 to-orange-600", border: "border-amber-400", word: "Monkey — ooh-ooh!", sound: "Monkey" },
      { front: "Cow", emoji: "🐮", color: "from-pink-300 to-rose-500", border: "border-pink-400", word: "Cow — moo!", sound: "Cow" },
      { front: "Pig", emoji: "🐷", color: "from-pink-400 to-rose-500", border: "border-pink-400", word: "Pig — oink!", sound: "Pig" },
      { front: "Horse", emoji: "🐴", color: "from-amber-700 to-stone-800", border: "border-amber-500", word: "Horse — neigh!", sound: "Horse" },
      { front: "Sheep", emoji: "🐑", color: "from-slate-200 to-slate-400", border: "border-slate-300", word: "Sheep — baa!", sound: "Sheep" },
      { front: "Frog", emoji: "🐸", color: "from-green-400 to-emerald-600", border: "border-green-400", word: "Frog — ribbit!", sound: "Frog" },
    ],
    Colors: [
      { front: "Red", emoji: "🍎", color: "from-red-400 to-red-700", border: "border-red-400", word: "Red" },
      { front: "Orange", emoji: "🍊", color: "from-orange-400 to-orange-600", border: "border-orange-400", word: "Orange" },
      { front: "Yellow", emoji: "🌞", color: "from-yellow-300 to-amber-500", border: "border-yellow-400", word: "Yellow" },
      { front: "Green", emoji: "🍀", color: "from-green-400 to-emerald-600", border: "border-green-400", word: "Green" },
      { front: "Blue", emoji: "🫐", color: "from-sky-400 to-blue-700", border: "border-sky-400", word: "Blue" },
      { front: "Purple", emoji: "🍇", color: "from-violet-400 to-purple-700", border: "border-violet-400", word: "Purple" },
      { front: "Pink", emoji: "🌸", color: "from-pink-300 to-pink-500", border: "border-pink-400", word: "Pink" },
      { front: "Brown", emoji: "🧸", color: "from-amber-700 to-stone-900", border: "border-amber-500", word: "Brown" },
      { front: "Black", emoji: "⚫", color: "from-zinc-700 to-black", border: "border-zinc-400", word: "Black" },
      { front: "White", emoji: "⚪", color: "from-slate-200 to-white", border: "border-slate-300", word: "White" },
    ],
    Shapes: [
      { front: "◯", emoji: "🔵", color: "from-sky-400 to-blue-700", border: "border-sky-400", word: "Circle" },
      { front: "▢", emoji: "🟧", color: "from-orange-400 to-orange-600", border: "border-orange-400", word: "Square" },
      { front: "△", emoji: "🔺", color: "from-red-400 to-red-600", border: "border-red-400", word: "Triangle" },
      { front: "▬", emoji: "📗", color: "from-green-400 to-emerald-600", border: "border-green-400", word: "Rectangle" },
      { front: "★", emoji: "⭐", color: "from-yellow-400 to-orange-500", border: "border-yellow-400", word: "Star" },
      { front: "♥", emoji: "💖", color: "from-pink-400 to-pink-600", border: "border-pink-400", word: "Heart" },
      { front: "◇", emoji: "💎", color: "from-cyan-400 to-sky-700", border: "border-cyan-400", word: "Diamond" },
      { front: "⬢", emoji: "🔶", color: "from-amber-400 to-yellow-700", border: "border-amber-400", word: "Hexagon" },
    ],
    "First Words": [
      { front: "Mom", emoji: "👩", color: "from-pink-400 to-pink-600", border: "border-pink-400", word: "Mom — I love you!" },
      { front: "Dad", emoji: "👨", color: "from-sky-400 to-blue-600", border: "border-sky-400", word: "Dad — I love you!" },
      { front: "Hi", emoji: "👋", color: "from-yellow-300 to-amber-500", border: "border-yellow-400", word: "Hi!" },
      { front: "Bye", emoji: "👋", color: "from-violet-400 to-purple-600", border: "border-violet-400", word: "Bye bye!" },
      { front: "Yes", emoji: "👍", color: "from-green-400 to-emerald-600", border: "border-green-400", word: "Yes, please!" },
      { front: "No", emoji: "🙅", color: "from-red-400 to-red-600", border: "border-red-400", word: "No thank you" },
      { front: "Love", emoji: "💖", color: "from-pink-400 to-fuchsia-600", border: "border-pink-400", word: "I love you" },
      { front: "Play", emoji: "🎈", color: "from-cyan-400 to-sky-700", border: "border-cyan-400", word: "Let's play!" },
      { front: "Eat", emoji: "🍎", color: "from-orange-400 to-red-600", border: "border-red-400", word: "Time to eat" },
      { front: "Sleep", emoji: "😴", color: "from-indigo-500 to-purple-700", border: "border-indigo-400", word: "Time to sleep" },
    ],
  }), []);
  const [tab, setTab] = useState("home"); // home | deck | match
  const [deck, setDeck] = useState("Letters");
  const [i, setI] = useState(0);

  const cards = DECKS[deck];
  const card = cards?.[i];

  const speak = (text) => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      if (voicePreset === "woman") {
        u.rate = 0.85; u.pitch = 1.05; u.volume = 1;
        const v = pickFemaleVoice();
        if (v) u.voice = v;
      } else {
        // "kid" — higher pitch, slower, gentle
        u.rate = 0.82; u.pitch = 1.5; u.volume = 1;
        const v = pickFemaleVoice();
        if (v) u.voice = v;
      }
      u.lang = "en-US";
      window.speechSynthesis?.cancel?.();
      window.speechSynthesis?.speak?.(u);
    } catch {}
  };
  // Intro: when "custom" preset, play the parent's recording once on open
  useEffect(() => {
    if (voicePreset === "custom" && customVoiceUrl && !introPlayed) {
      try {
        const audio = new Audio(customVoiceUrl);
        audio.play().catch(() => {});
        setIntroPlayed(true);
      } catch {}
    }
  // eslint-disable-next-line
  }, [voicePreset, customVoiceUrl, introPlayed]);
  const next = () => {
    setI((n) => {
      const nn = (n + 1) % cards.length;
      // log progress — completing a deck (wrap to 0)
      if (nn === 0) try { api.post("/kids/learn/progress", { deck, completed: true }); } catch {}
      return nn;
    });
  };
  const prev = () => setI((n) => (n - 1 + cards.length) % cards.length);
  useEffect(() => {
    const kh = (e) => {
      if (tab !== "deck") return;
      if (e.key === "ArrowRight" || e.key === " ") { next(); }
      else if (e.key === "ArrowLeft") { prev(); }
      else if (e.key === "Escape") { setTab("home"); }
    };
    window.addEventListener("keydown", kh);
    return () => window.removeEventListener("keydown", kh);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, cards?.length]);
  useEffect(() => {
    if (tab === "deck" && card) speak(`${card.front}. ${card.word}`);
  // eslint-disable-next-line
  }, [i, deck, tab]);

  const openDeck = (k) => { setDeck(k); setI(0); setTab("deck"); };

  return (
    <div data-testid="kids-flashcards-overlay" className="fixed inset-0 z-50 flex flex-col items-center overflow-y-auto" style={{
      background: "radial-gradient(ellipse at 30% 0%, #2563eb 0%, transparent 55%), radial-gradient(ellipse at 80% 100%, #ec4899 0%, transparent 55%), linear-gradient(180deg, #1e1b4b 0%, #2a1d6d 60%, #3b0764 100%)"
    }}>
      {/* Top bar */}
      <div className="sticky top-0 z-20 w-full max-w-[1400px] flex items-center justify-between p-[2vmin] pb-[1vmin]">
        <img src="/kids-learn-logo.png" alt="Tap IN Learn and Play" className="h-[10vmin] max-h-24 w-auto drop-shadow-[0_4px_16px_rgba(255,255,255,0.2)]" draggable={false} />
        <div className="flex gap-[1vmin]">
          <button data-tv-focus="true" data-testid="flashcards-home" onClick={() => setTab("home")}
            className={`tv-focusable kids-focusable px-[2vmin] py-[1vmin] rounded-full font-black text-[1.8vmin] ${tab === "home" ? "bg-yellow-300 text-[#222]" : "bg-white/15 text-white hover:bg-white/25"}`}>Home</button>
          <button data-tv-focus="true" data-testid="flashcards-match" onClick={() => setTab("match")}
            className={`tv-focusable kids-focusable px-[2vmin] py-[1vmin] rounded-full font-black text-[1.8vmin] ${tab === "match" ? "bg-yellow-300 text-[#222]" : "bg-white/15 text-white hover:bg-white/25"}`}>Match Game</button>
          <button data-tv-focus="true" data-testid="flashcards-close" onClick={onClose}
            className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[42px] min-h-[42px] rounded-full bg-white/90 text-[#222] flex items-center justify-center shadow-lg"><X className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
        </div>
      </div>

      {/* HOME — banner + deck tiles */}
      {tab === "home" && (
        <div className="w-full max-w-[1400px] px-[2vmin] pb-[4vmin]">
          {/* Official Learn & Play banner */}
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-4 ring-white/30 mb-[2.4vmin]">
            <img src="/kids-learn-banner.png" alt="Learn and Play — Tap IN Kids Network" className="w-full h-auto block select-none" draggable={false} />
          </div>

          {/* Deck tiles */}
          <h2 className="kids-h text-white font-display font-black text-[3vmin] mb-[1.4vmin]">Pick a deck 📚</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-[1.4vmin]">
            {[
              { k: "Letters", emoji: "🔤", g: "from-emerald-400 to-emerald-700", sub: "ABC" },
              { k: "Numbers", emoji: "🔢", g: "from-sky-400 to-blue-700", sub: "123" },
              { k: "Animals", emoji: "🐾", g: "from-amber-400 to-orange-600", sub: "Roar!" },
              { k: "Colors", emoji: "🌈", g: "from-pink-400 to-fuchsia-600", sub: "Hue" },
              { k: "Shapes", emoji: "🔷", g: "from-violet-400 to-purple-700", sub: "Geometry" },
              { k: "First Words", emoji: "💬", g: "from-rose-400 to-red-600", sub: "Say it" },
            ].map((d) => (
              <button key={d.k} data-tv-focus="true" data-testid={`flashcards-deck-${d.k}`} onClick={() => openDeck(d.k)}
                className={`tv-focusable kids-focusable kids-card-scale relative aspect-[4/5] rounded-[1.5rem] bg-gradient-to-br ${d.g} text-white font-display font-black shadow-xl ring-4 ring-white/30 flex flex-col items-center justify-center gap-[0.8vmin] p-[1.4vmin] overflow-hidden`}>
                <img src="/kids-learn-logo.png" alt="" aria-hidden className="absolute top-[0.8vmin] right-[0.8vmin] h-[6vmin] max-h-16 min-h-[38px] w-auto opacity-100 drop-shadow pointer-events-none" draggable={false} />
                <div className="text-[6vmin] drop-shadow-[0_4px_0_rgba(0,0,0,0.18)]">{d.emoji}</div>
                <div className="text-[2.2vmin] text-center leading-tight">{d.k}</div>
                <div className="text-[1.4vmin] text-white/80">{d.sub}</div>
                <div className="text-[1.3vmin] bg-white/20 px-[1.2vmin] py-[0.3vmin] rounded-full">{DECKS[d.k]?.length} cards</div>
              </button>
            ))}
          </div>

          {/* Activity row — Match game tile */}
          <h2 className="kids-h text-white font-display font-black text-[3vmin] mt-[3vmin] mb-[1.4vmin]">Play a game 🎮</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[1.4vmin]">
            <button data-tv-focus="true" data-testid="activity-match" onClick={() => setTab("match")}
              className="tv-focusable kids-focusable kids-card-scale aspect-[5/3] rounded-[1.5rem] bg-gradient-to-br from-fuchsia-500 via-rose-500 to-amber-400 text-white font-display font-black shadow-xl ring-4 ring-white/30 flex flex-col items-center justify-center gap-[0.6vmin] p-[1.6vmin]">
              <div className="text-[7vmin] drop-shadow-[0_4px_0_rgba(0,0,0,0.18)]">🧩</div>
              <div className="text-[2.4vmin]">Matching Game</div>
              <div className="text-[1.6vmin] text-white/85">Tap the pair that matches!</div>
            </button>
            <button data-tv-focus="true" data-testid="activity-story" onClick={() => openDeck("First Words")}
              className="tv-focusable kids-focusable kids-card-scale aspect-[5/3] rounded-[1.5rem] bg-gradient-to-br from-sky-400 to-blue-700 text-white font-display font-black shadow-xl ring-4 ring-white/30 flex flex-col items-center justify-center gap-[0.6vmin] p-[1.6vmin]">
              <div className="text-[7vmin] drop-shadow-[0_4px_0_rgba(0,0,0,0.18)]">📖</div>
              <div className="text-[2.4vmin]">First Words</div>
              <div className="text-[1.6vmin] text-white/85">Read &amp; say aloud</div>
            </button>
            <button data-tv-focus="true" data-testid="activity-animals" onClick={() => openDeck("Animals")}
              className="tv-focusable kids-focusable kids-card-scale aspect-[5/3] rounded-[1.5rem] bg-gradient-to-br from-amber-400 to-orange-600 text-white font-display font-black shadow-xl ring-4 ring-white/30 flex flex-col items-center justify-center gap-[0.6vmin] p-[1.6vmin]">
              <div className="text-[7vmin] drop-shadow-[0_4px_0_rgba(0,0,0,0.18)]">🦁</div>
              <div className="text-[2.4vmin]">Animal Sounds</div>
              <div className="text-[1.6vmin] text-white/85">Roar, moo &amp; more!</div>
            </button>
          </div>
        </div>
      )}

      {/* DECK — single card view */}
      {tab === "deck" && card && (
        <div className="flex-1 w-full max-w-5xl flex items-center justify-center p-[2vmin]">
          <button data-tv-focus="true" data-testid="flashcards-prev" onClick={prev}
            className="tv-focusable kids-focusable mr-[2vmin] w-[7vmin] h-[7vmin] min-w-[48px] min-h-[48px] rounded-full bg-white/90 text-[#222] font-black text-[3vmin] shadow-xl">‹</button>

          <div data-testid="flashcards-card" className={`relative flex-1 max-w-[64vmin] aspect-[4/5] rounded-[2.4rem] bg-white text-[#222] shadow-2xl border-[8px] ${card.border} flex flex-col items-center justify-center gap-[1.2vmin] overflow-hidden p-[2vmin]`}>
            <div className={`absolute inset-x-0 top-0 h-[12%] bg-gradient-to-r ${card.color}`} />
            {/* Learn & Play watermark — larger, bolder corner stamp */}
            <img src="/kids-learn-logo.png" alt="" aria-hidden className="absolute top-[1.4vmin] right-[1.4vmin] h-[11vmin] max-h-[120px] min-h-[56px] w-auto opacity-100 drop-shadow-[0_6px_12px_rgba(0,0,0,0.25)] pointer-events-none" draggable={false} />
            <div className={`text-[14vmin] font-black leading-none bg-gradient-to-br ${card.color} bg-clip-text text-transparent drop-shadow-[0_6px_0_rgba(0,0,0,0.08)] mt-[2vmin]`}>{card.front}</div>
            <div className="text-[16vmin] leading-none">{card.emoji}</div>
            <div className="text-[3.2vmin] font-display font-black">{card.word}</div>
            <button data-tv-focus="true" data-testid="flashcards-speak" onClick={() => speak(`${card.front}. ${card.word}`)}
              className={`tv-focusable kids-focusable inline-flex items-center gap-[1vmin] text-white font-black px-[2vmin] py-[1vmin] rounded-full text-[1.8vmin] bg-gradient-to-br ${card.color} shadow`}
            ><Volume2 className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /> Say it</button>
            {/* Footer labels tinted to match the card's own gradient for a
                cohesive look (no more grey/black hardcoded text). */}
            <div className={`absolute bottom-[1.4vmin] right-[1.6vmin] text-[1.6vmin] font-black bg-gradient-to-r ${card.color} bg-clip-text text-transparent`}>{i + 1} / {cards.length}</div>
            <div className={`absolute bottom-[1.4vmin] left-[1.6vmin] text-[1.3vmin] font-black uppercase tracking-[0.18em] bg-gradient-to-r ${card.color} bg-clip-text text-transparent`}>{deck}</div>
          </div>

          <button data-tv-focus="true" data-testid="flashcards-next" onClick={next}
            className="tv-focusable kids-focusable ml-[2vmin] w-[7vmin] h-[7vmin] min-w-[48px] min-h-[48px] rounded-full bg-white/90 text-[#222] font-black text-[3vmin] shadow-xl">›</button>
        </div>
      )}

      {/* MATCH GAME */}
      {tab === "match" && <KidsMatchGame DECKS={DECKS} speak={speak} />}

      <div className="text-white/60 text-[1.5vmin] pb-[2vmin]">Use ← → on your remote · Space to flip · Esc to go back</div>
    </div>
  );
}

/** Simple matching mini-game: pick the emoji that matches the big word. */
function KidsMatchGame({ DECKS, speak }) {
  const ALL = useMemo(() => [...DECKS.Animals, ...DECKS.Colors, ...DECKS.Shapes], [DECKS]);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [locked, setLocked] = useState(false);
  const [feedback, setFeedback] = useState(null); // "correct" | "wrong" | null

  const puzzle = useMemo(() => {
    if (!ALL.length) return null;
    const correct = ALL[Math.floor(Math.random() * ALL.length)];
    const pool = ALL.filter((x) => x.front !== correct.front);
    const distractors = [];
    while (distractors.length < 3 && pool.length) {
      const idx = Math.floor(Math.random() * pool.length);
      distractors.push(pool.splice(idx, 1)[0]);
    }
    const options = [correct, ...distractors].sort(() => Math.random() - 0.5);
    return { correct, options };
  // eslint-disable-next-line
  }, [round]);
  useEffect(() => { if (puzzle) speak(`Find the ${puzzle.correct.front}`); /* eslint-disable-next-line */ }, [round]);

  const pick = (opt) => {
    if (locked) return;
    setLocked(true);
    if (opt.front === puzzle.correct.front) {
      setFeedback("correct");
      setScore((s) => s + 10 + streak * 2);
      setStreak((s) => s + 1);
      speak(`Yes! ${opt.front}!`);
      try { api.post("/kids/learn/progress", { game: "match", correct: true }); } catch {}
    } else {
      setFeedback("wrong");
      setStreak(0);
      speak(`Oops, try again!`);
      try { api.post("/kids/learn/progress", { game: "match", correct: false }); } catch {}
    }
    setTimeout(() => { setFeedback(null); setLocked(false); setRound((r) => r + 1); }, 1100);
  };

  if (!puzzle) return null;

  return (
    <div data-testid="kids-match-game" className="flex-1 w-full max-w-5xl p-[2vmin] text-white">
      <div className="flex items-center justify-between mb-[2vmin]">
        <div className="inline-flex items-center gap-[1.2vmin]">
          <div className="px-[1.6vmin] py-[0.6vmin] rounded-full bg-yellow-300 text-[#222] font-black text-[1.8vmin]">Score {score}</div>
          <div className="px-[1.6vmin] py-[0.6vmin] rounded-full bg-fuchsia-500 text-white font-black text-[1.8vmin]">🔥 {streak}</div>
        </div>
        <div className="text-white/70 text-[1.6vmin]">Round #{round + 1}</div>
      </div>

      <div className="rounded-[2rem] bg-white/10 backdrop-blur ring-2 ring-white/20 p-[2.4vmin] text-center shadow-2xl">
        <div className="text-white/80 text-[2vmin] uppercase tracking-[0.22em] font-black mb-[1vmin]">Find the…</div>
        <div className={`text-[7vmin] font-display font-black drop-shadow-[0_6px_0_rgba(0,0,0,0.2)] ${feedback === "correct" ? "text-emerald-300 animate-pulse" : feedback === "wrong" ? "text-red-300" : "text-white"}`}>
          {puzzle.correct.front}
          {feedback === "correct" && " ✨"}
        </div>
        <div className="text-white/70 text-[2vmin] mt-[0.6vmin]">{puzzle.correct.word}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-[1.6vmin] mt-[2.4vmin]">
        {puzzle.options.map((opt, idx) => {
          const isRightChoice = feedback && opt.front === puzzle.correct.front;
          return (
            <button
              key={`${round}-${idx}`}
              data-tv-focus="true"
              data-testid={`match-option-${idx}`}
              disabled={locked}
              onClick={() => pick(opt)}
              className={`tv-focusable kids-focusable kids-card-scale relative aspect-[4/5] rounded-[1.6rem] bg-white shadow-xl ring-[6px] flex flex-col items-center justify-center gap-[1vmin] overflow-hidden p-[1.4vmin] ${opt.border} ${isRightChoice ? "ring-emerald-400 animate-pulse" : ""}`}
            >
              <img src="/kids-learn-logo.png" alt="" aria-hidden className="absolute top-[0.8vmin] right-[0.8vmin] h-[5.6vmin] max-h-14 min-h-[38px] w-auto opacity-100 drop-shadow pointer-events-none" draggable={false} />
              <div className={`text-[14vmin] leading-none drop-shadow`}>{opt.emoji}</div>
              <div className="text-[2vmin] font-display font-black text-[#333]">{opt.front}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * KidsSleepTimer — small popover menu to start a countdown. When it
 * reaches zero, fires `onFire()` so the parent can lock the screen.
 */
export function KidsSleepTimer({ remaining, setRemaining, onClose, onFire }) {
  useEffect(() => {
    if (remaining == null) return;
    if (remaining <= 0) { onFire?.(); return; }
    const t = setInterval(() => setRemaining((r) => (r != null ? r - 1 : r)), 60_000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  const OPTIONS = [15, 30, 45, 60, 90];
  return (
    <div data-testid="kids-sleep-menu" className="absolute right-[var(--kids-safe-x)] top-[calc(var(--kids-safe-y)+5.8em)] z-40 bg-[#1b1140] text-white rounded-[1.4rem] shadow-2xl ring-2 ring-white/10 p-[2vmin] min-w-[40vmin] max-w-[90vw]">
      <div className="flex items-center justify-between mb-[1.4vmin]">
        <div className="inline-flex items-center gap-[1vmin] font-display font-black text-[2.2vmin]"><Moon className="w-[2.4vmin] h-[2.4vmin] min-w-[18px] text-yellow-300" /> Sleep timer</div>
        <button data-tv-focus="true" onClick={onClose} className="tv-focusable kids-focusable w-[4vmin] h-[4vmin] min-w-[34px] min-h-[34px] rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X className="w-[2vmin] h-[2vmin] min-w-[16px]" /></button>
      </div>
      {remaining != null ? (
        <div className="text-center py-[1vmin]">
          <div className="text-[6vmin] font-black drop-shadow">{remaining} <span className="text-[2.4vmin] font-bold text-white/60">min</span></div>
          <div className="text-white/60 text-[1.6vmin] mb-[1.6vmin]">Bedtime starts when the timer hits zero.</div>
          <button data-tv-focus="true" data-testid="kids-sleep-cancel" onClick={() => setRemaining(null)}
            className="tv-focusable kids-focusable bg-white/15 hover:bg-white/25 text-white font-black px-[2.2vmin] py-[1vmin] rounded-full text-[1.8vmin]">Cancel timer</button>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-[1vmin]">
          {OPTIONS.map((m) => (
            <button key={m} data-tv-focus="true" data-testid={`kids-sleep-${m}`} onClick={() => setRemaining(m)}
              className="tv-focusable kids-focusable bg-white/10 hover:bg-pink-500 hover:text-white text-white font-black py-[1.2vmin] rounded-[1rem] text-[1.8vmin]">{m} min</button>
          ))}
        </div>
      )}
    </div>
  );
}
